// src/pages/TeachingPage.tsx
import React, { useCallback, useEffect, useMemo } from "react";
import { VStack, useToast } from "@chakra-ui/react";
import AudioCard from "../../components/cards/AudioCard";
import CustomLoader from "../../components/placeholders/CustomLoader";
import {
  CARD_SPACING,
  DEFAULT_WIDTH,
  PAGE_PADDING,
} from "../../helpers/constant";
import FilterBar from "../../components/cards/FilterBar";
import { useRecordings } from "../../context/RecordingContext";
import EmptyState from "../../components/placeholders/EmptyState";
import { extractApiErrorMessage, formatDate } from "../../helpers/utils";
import { GraduationCapIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { useSharedRecordingQuery } from "../../hooks/useSharedRecordingQuery";
import { Recording } from "../../model/recording";

const TeachingPage: React.FC = () => {
  const toast = useToast();
  const { t, i18n } = useTranslation();
  const {
    isLoading,
    rubrics,
    hasInitialized,
    setHasInitialized,
    selectedRubricIDs,
    setSelectedRubricIDs,
    recordingsByCategory,
    recordingsByRubric,
    listRubrics,
    listFavoriteIDs,
    listRecordingsByCategory,
    listRecordingsByRubric,
    addFavorite,
    removeFavorite,
    isFavorite,
    getRecordingFiles,
  } = useRecordings();

  const selectedRubricID = selectedRubricIDs.teaching ?? "";
  const {
    searchMode,
    searchQuery,
    setSearchQuery,
    sharedRecording,
    isLoadingSharedRecording,
    selectSharedRecording,
  } = useSharedRecordingQuery({
    matchRecording: (recording: Recording) => recording.category === "teaching",
    onLoadError: (error: unknown) => {
      toast({
        title: t("pages.teachings.loadErrorTitle"),
        description: extractApiErrorMessage(
          error,
          String(t("common.tryAgain")),
        ),
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    },
  });

  useEffect(() => {
    if (hasInitialized.teaching) {
      return;
    }

    let isMounted = true;

    const initialize = async () => {
      try {
        const [fetchedRubrics] = await Promise.all([
          listRubrics(),
          listFavoriteIDs(),
        ]);

        const teachingRubrics = fetchedRubrics.filter(
          (r) => r.category === "teaching",
        );

        const mostRecent =
          teachingRubrics.length > 0 ? teachingRubrics[0] : null;

        const initialRubricID = selectedRubricID || mostRecent?.rubricID || "";

        if (initialRubricID) {
          setSelectedRubricIDs((prev) => ({
            ...prev,
            teaching: initialRubricID,
          }));
          await listRecordingsByRubric("teaching", initialRubricID);
        } else {
          await listRecordingsByCategory("teaching");
        }
      } catch (error) {
        toast({
          title: t("pages.teachings.loadErrorTitle"),
          description: extractApiErrorMessage(
            error,
            String(t("common.tryAgain")),
          ),
          status: "error",
          duration: 4000,
          isClosable: true,
          position: "top",
        });
      } finally {
        if (isMounted)
          setHasInitialized((prev) => ({ ...prev, teaching: true }));
      }
    };

    initialize();

    return () => {
      isMounted = false;
    };
  }, [
    hasInitialized.teaching,
    listRubrics,
    listFavoriteIDs,
    listRecordingsByRubric,
    listRecordingsByCategory,
    selectedRubricID,
    setHasInitialized,
    setSelectedRubricIDs,
    t,
    toast,
  ]);

  const handleRubricChange = useCallback(
    async (rubricID: string) => {
      try {
        setSelectedRubricIDs((prev) => ({ ...prev, teaching: rubricID }));

        if (rubricID) {
          await listRecordingsByRubric("teaching", rubricID);
          return;
        }

        await listRecordingsByCategory("teaching");
      } catch (error) {
        toast({
          title: rubricID
            ? t("pages.teachings.filterErrorTitle")
            : t("pages.teachings.loadErrorTitle"),
          description: extractApiErrorMessage(
            error,
            String(t("common.tryAgain")),
          ),
          status: "error",
          duration: 4000,
          isClosable: true,
          position: "top",
        });
      }
    },
    [
      listRecordingsByCategory,
      listRecordingsByRubric,
      setSelectedRubricIDs,
      t,
      toast,
    ],
  );

  const rubricKey = selectedRubricID ? `teaching:${selectedRubricID}` : "";

  const scopedRecordings = useMemo(
    () =>
      selectedRubricID
        ? (recordingsByRubric[rubricKey] ?? [])
        : (recordingsByCategory.teaching ?? []),
    [
      recordingsByCategory.teaching,
      recordingsByRubric,
      rubricKey,
      selectedRubricID,
    ],
  );

  const displayedRecordings = useMemo(
    () => (sharedRecording ? [sharedRecording] : scopedRecordings),
    [scopedRecordings, sharedRecording],
  );

  const teachingRubrics = useMemo(
    () => rubrics.filter((rubric) => rubric.category === "teaching"),
    [rubrics],
  );

  const hasNoData =
    !selectedRubricID && (recordingsByCategory.teaching ?? []).length === 0;
  const hasNoResults = displayedRecordings.length === 0 && !hasNoData;

  return (
    <VStack w={DEFAULT_WIDTH} px={PAGE_PADDING} mx="auto" align="start">
      <FilterBar
        onSearch={setSearchQuery}
        searchValue={searchQuery}
        rubrics={teachingRubrics}
        selectedRubricID={selectedRubricID}
        onRubricChange={handleRubricChange}
        rubricSelectId="onboarding-teaching-theme-select"
        placeholder={String(t("pages.teachings.searchPlaceholder"))}
        searchMode={searchMode}
        onSuggestionSelect={(item) => selectSharedRecording(item.recordingID)}
      />

      {isLoading || isLoadingSharedRecording || !hasInitialized["teaching"] ? (
        <CustomLoader />
      ) : hasNoData ? (
        <EmptyState
          icon={GraduationCapIcon}
          title={t("pages.teachings.emptyTitle")}
          description={t("pages.teachings.emptyDescription")}
        />
      ) : hasNoResults ? (
        <EmptyState
          icon={GraduationCapIcon}
          title={t("pages.search.noResultsTitle")}
          description={t("pages.search.noResultsDescription")}
        />
      ) : (
        <VStack spacing={CARD_SPACING} w="full">
          {displayedRecordings.map((recording) => (
            <AudioCard
              key={recording.recordingID}
              recordingID={recording.recordingID}
              title={recording.title}
              preacherName={recording.preacherName}
              date={formatDate(
                recording.date,
                i18n.language.startsWith("fr") ? "fr" : "en",
              )}
              hasRecording={Boolean(recording.recordingS3Key)}
              hasSummary={Boolean(recording.summaryS3Key)}
              youtubeLink={recording.youtubeLink}
              saved={isFavorite(recording.recordingID)}
              onAddFavorite={() =>
                addFavorite(recording.recordingID, recording)
              }
              onRemoveFavorite={removeFavorite}
              onRequestFiles={(recordingID) => getRecordingFiles(recordingID)}
            />
          ))}
        </VStack>
      )}
    </VStack>
  );
};

export default TeachingPage;
